import { useState, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import Webcam from 'react-webcam';
import { Button, Checkbox, Result, Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import PermissionsModal from '../PermissionsModal';
import { AIDisclaimerModal } from '../../RulesScreen';
import { useImgComparison } from 'hooks';
import { detectFacesInImage } from 'utils/faceDetection';
import { checkImgProctoringRecency, classifyMediaDeviceError, getPrivacyPolicyLink } from 'utils/helper';
import { storage } from 'services/config/storage';
import { Camera, Dropdown } from 'assets/icons';
import { HIDE_GAME_IFRAME_EVENT, PAUSE_TIMER_EVENT, RESUME_TIMER_EVENT, SHOW_GAME_IFRAME_EVENT } from 'constants/helperModalConstants';
import { PERMISSION_DEVICE_TYPES, PERMISSION_ERRORS, AI_DISCLAIMER_ORG_IDS } from 'constants/globalConstants';
import { useTranslation } from 'react-i18next';
import { useGameplayConfig } from 'store';

const WebcamCapture = forwardRef(({
  initialCapture = false,
  isMobile = false,
  snapshotInterval = 60,
  imgSrc,
  setImgSrc,
  confirmPhoto,
  loading,
  linkId,
  consent,
  setConsent,
  isVideoProctored = false,
}, ref) => {
  const webcamRef = useRef(null);

  const { t } = useTranslation();

  const { configLanguageSettings: { isRtlLayout } } = useGameplayConfig()

  const isChrome = /Chrome/.test(navigator.userAgent);
  const privacyPolicyLink = getPrivacyPolicyLink();
  const orgId = storage.get.orgId();
  const showAIDisclaimer = AI_DISCLAIMER_ORG_IDS.includes(orgId);

  const [webCamKey, setWebCamKey] = useState(0); // for refreshing WebCam component (see useEffect for more info)
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [devices, setDevices] = useState(null);
  const [imageAnalysisData, setImageAnalysisData] = useState(null);
  const [storedDeviceId, setStoredDeviceId] = useState(storage.get.webCam());
  // Check camera permission on component mount -> For image proctoring where it does not trigger immediately on refresh
  const [onMountCamCheck, setOnMountCamCheck] = useState(true);
  const [termsOfUseConsent, setTermsOfUseConsent] = useState(false);
  const [showAIDisclaimerModal, setShowAIDisclaimerModal] = useState(false);

  const { proctorGameplayCapture } = useImgComparison({ linkId, setShowPermissionModal });

  const saveWebCamDevice = (deviceId) => {
    storage.set.webCam(deviceId)
    setStoredDeviceId(deviceId)
  }

  const handleDevices = (mediaDevices, updateDeviceId = false) => {
    const videoSources = mediaDevices.filter(({ kind, label }) => kind === 'videoinput' && !label.toLowerCase().includes('back') && !label.toLowerCase().includes('obs'))
    setDevices(videoSources)
    if (videoSources.length === 0) throw new Error(PERMISSION_ERRORS.NO_DEVICE)
    if (updateDeviceId) {
      const [firstSource] = videoSources ?? [{}]
      saveWebCamDevice(firstSource.deviceId)
    }
  }

  const checkCameraStatus = async (selectedCamDevice) => {
    await navigator.mediaDevices.getUserMedia({
      video: {
        deviceId: selectedCamDevice,
      },
      audio: false,
    })
    if (onMountCamCheck) setOnMountCamCheck(false);
  }

  const capture = async () => {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices()
      handleDevices(mediaDevices)
      const selectedCamDevice = storedDeviceId || storage.get.webCam()
      await checkCameraStatus(selectedCamDevice);
      const initialCamDevice = mediaDevices.filter(device => device.deviceId === selectedCamDevice)
      if (initialCamDevice.length === 0) throw new Error(PERMISSION_ERRORS.NO_DEVICE)
      else {
        const imageSrc = webcamRef.current.getScreenshot()
        setImgSrc(imageSrc)
        if (initialCapture) {
          proctorInitialCapture(imageSrc)
        } else {
          proctorGameplayCapture(imageSrc)
        }
      }
    } catch (err) {
      console.log('Error in camera permission', err)
      const classifiedError = classifyMediaDeviceError(err?.toString())
      setShowPermissionModal(classifiedError)
    }
  }

  const onRetake = useCallback(() => {
    setImgSrc('');
    setImageAnalysisData(null);
    setTermsOfUseConsent(false);
  }, []);

  const proctorInitialCapture = async (imageSrc) => {
    const data = await detectFacesInImage(imageSrc)
    setImageAnalysisData(data)
  }

  const showErrorModal = (err) => {
    setShowPermissionModal(err)
  }

  useEffect(() => {
    let intervalId
    (async () => {
      if (initialCapture) {
        try {
          const mediaDevices = await navigator.mediaDevices.enumerateDevices()
          handleDevices(mediaDevices, true)
        } catch (err) { console.log(err) }
      }
      else {
        try {
          const mediaDevices = await navigator.mediaDevices.enumerateDevices()
          handleDevices(mediaDevices)
          if (onMountCamCheck) {
            const selectedCamDevice = storedDeviceId || storage.get.webCam()
            await checkCameraStatus(selectedCamDevice);
          };
          intervalId = setInterval(() => {
            try {
              const isStale = checkImgProctoringRecency(linkId)
              if (isStale) throw new Error(PERMISSION_ERRORS.API_FAILURE)
              capture()
            } catch (err) {
              console.log(err)
              setShowPermissionModal(PERMISSION_ERRORS.API_FAILURE)
            }
          }, 1 * snapshotInterval * 1000)
        } catch (err) {
          console.log(err)
          const classifiedError = classifyMediaDeviceError(err?.toString())
          setShowPermissionModal(classifiedError)
        }
      }
    })()
    return () => clearInterval(intervalId)
  }, [initialCapture, snapshotInterval])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // System has woken up from sleep mode, so force a refresh of the Webcam component
        setWebCamKey(webCamKey + 1);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [webCamKey]);

  useEffect(() => {
    if (showPermissionModal) {
      window.dispatchEvent(new Event(PAUSE_TIMER_EVENT))
      window.dispatchEvent(new Event(HIDE_GAME_IFRAME_EVENT))
    } else {
      window.dispatchEvent(new Event(RESUME_TIMER_EVENT))
      window.dispatchEvent(new Event(SHOW_GAME_IFRAME_EVENT))
    }
  }, [showPermissionModal])

  useImperativeHandle(ref, () => ({
    showErrorModal,
  }))

  return (
    <>
      <style>{`
        .terms-of-use-link:hover {
          color: #5b9bd5 !important;
        }
      `}</style>
      {initialCapture ? (
        <div className="flex flex-col">
          <div className="flex justify-center w-full">
            {imgSrc ? (
              <div className="flex flex-col justify-center items-center">
                <img
                  src={imgSrc}
                  alt="user"
                  style={{
                    width: isMobile ? '250px' : '300px',
                    height: isMobile ? '250px' : '300px',
                    borderRadius: '50%',
                  }}
                />
                {imageAnalysisData !== null && (
                  <button
                    className="flex items-center font-medium br-20 mt-10"
                    style={{
                      color: 'white',
                      background: 'none',
                      borderStyle: 'none',
                      cursor: 'pointer',
                    }}
                    onClick={onRetake}
                  >
                    <span className='flex items-center mb-4 mr-4'>
                      <Camera />
                    </span>
                    {' '}
                    {t("webcam.take_again")}
                  </button>
                )}

                {imageAnalysisData === null ? (
                  <>
                    <LoadingOutlined className="size-24 mt-20" spin />
                    <p className="mt-10 size-14 font-light opacity-70 color-primary">
                      {t("webcam.clicked_loader_text")}
                    </p>
                  </>
                ) : imageAnalysisData?.isImageCritical ? (
                  <>
                    <p className="color-danger align-center size-14 font-medium mt-10 mb-10">
                      {`${imageAnalysisData.criticalReason} ${imageAnalysisData.criticalReason?.startsWith("Make") ? "" : !isRtlLayout ? `, ${t("try_again")} ${!isChrome ? `in Google Chrome` : ""}` : ""} `}
                    </p>
                  </>
                ) : (
                  <div className="mt-20 flex flex-col items-center">
                    {showAIDisclaimer && (
                      <div className="flex items-center relative right-6">
                        <Checkbox
                          checked={termsOfUseConsent}
                          onChange={(e) => setTermsOfUseConsent(e.target.checked)}
                          className={`${isRtlLayout ? "ml-14" : "mr-14"} relative bottom-8`}
                        />
                        <p className="size-18 color-secondary">
                          By checking this box, I agree to the{' '}
                          <span
                            className="size-18 font-bold color-primary cursor-pointer terms-of-use-link"
                            onClick={() => setShowAIDisclaimerModal(true)}
                            style={{ cursor: 'pointer' }}
                          >
                            terms of use
                          </span>
                          .
                        </p>
                      </div>
                    )}
                    <div className="flex items-start mb-10">
                      <Checkbox
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className={`${isRtlLayout ? "ml-10" : "mr-10"} circular-checkbox`}
                      />
                      <p className="size-18 color-secondary m-2">
                        {t("webcam.agree_to_privacy_policy")}
                        <a
                          className="size-18 font-bold color-primary"
                          href={privacyPolicyLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {' '}
                          {t("webcam.privacy_policy")}
                        </a>
                        .
                      </p>
                    </div>
                    <div className="mt-20">
                      <Button
                        type="primary"
                        className="font-medium br-20"
                        style={{
                          width: '150px',
                        }}
                        onClick={() => confirmPhoto({ cameraSupport: true })}
                        loading={loading}
                        disabled={imageAnalysisData?.isImageCritical || !consent || (showAIDisclaimer && !termsOfUseConsent)}
                      >
                        {t("webcam.continue")}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center">
                {devices === null ? (
                  <LoadingOutlined className="size-24 mt-20" spin />
                ) : devices.length === 0 ? (
                  <>
                    <Result
                      status="warning"
                      title={
                        <p className="color-primary size-18">
                          {
                            `${t("webcam.blocking_warning")}`
                          }
                        </p>
                      }
                      extra={<></>}
                    />
                  </>
                ) : (
                  <>
                    <Webcam
                      key={webCamKey}
                      muted={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      mirrored
                      onError={(error) => {
                        console.error('Error in webcam: ', error);
                      }}
                      style={{
                        borderRadius: isMobile ? '50%' : '20px',
                        width: isMobile ? '250px' : '350px',
                        height: isMobile ? '250px' : '350px',
                      }}
                      videoConstraints={{
                        width: 512,
                        height: 512,
                        facingMode: 'user',
                        deviceId: storedDeviceId,
                      }}
                    />
                    <p className="size-14 align-center font-medium opacity-70 color-primary mt-10">
                      {t("webcam.video_source")}
                    </p>
                    <div style={{ position: 'relative' }}>
                      <Select
                        className="size-14 max-width-300"
                        suffixIcon={<span style={{ transform: 'scale(0.7)', display: 'flex' }}><Dropdown /></span>}
                        popupMatchSelectWidth={true}
                        getPopupContainer={(trigger) => trigger.parentNode}
                        value={storedDeviceId}
                        options={devices.map(({ label, deviceId }) => ({
                          label,
                          value: deviceId,
                        }))}
                        onChange={saveWebCamDevice}
                      />
                    </div>
                    <div className="mt-40">
                      <Button
                        type="primary"
                        className="font-medium br-20"
                        style={{
                          width: '150px',
                        }}
                        disabled={!storedDeviceId}
                        onClick={capture}
                      >
                        {t("webcam.click_picture")}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <Webcam
          style={{
            position: 'absolute',
            right: '100%',
          }}
          key={webCamKey}
          muted={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          mirrored
          videoConstraints={{
            deviceId: storedDeviceId,
          }}
        />
      )}
      <PermissionsModal
        visible={isVideoProctored ? false : !!showPermissionModal}
        type={showPermissionModal}
        device={PERMISSION_DEVICE_TYPES.CAMERA}
      />
      <AIDisclaimerModal
        visible={showAIDisclaimerModal}
        onClose={() => setShowAIDisclaimerModal(false)}
      />
    </>
  );
});

export default WebcamCapture;
